import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, IconButton } from '@mui/material';
import { ChevronRightRounded, OfflineBoltRounded } from '@mui/icons-material';
import {
  getCategoryLabel,
  getFrameVoiceUrl,
  getVoiceStatusLabel,
  getVoiceSubStatusLabel,
} from '@src/services/voiceCloning';
import {
  VOICE_STATUS,
  VOICE_STATUS_COLORS,
  SUB_STATUS,
  VOICE_NOT_USED,
  STEP_VOICE_DETAIL,
  SUB_STATUS_COLORS,
  VOICE_CLONE_TYPE,
} from '@src/constants/voiceCloning';
import actions from '@src/redux/actions';
import route from '@src/constants/route';
import { LANDING_PAGE_URL } from '@src/configs';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { checkUserHasProvidedEmail } from '@src/services/email';
import CommonAvatar from '@src/new-components/Avatar';
import { COLOR } from '@src/styles/color';
import CommonButton from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import verifiedIcon from '@src/assets/icons/verified-rounded.svg';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import WaitingForRemovalTag from '../WaitingForRemovalTag';
import ActionMenuPopover from './ActionMenuPopover';
import { StyledVoiceItemCardNew, StyledChip } from './index.style';

const VoiceItemCardNew = ({
  id,
  avatar,
  name,
  category = {},
  status,
  gender,
  subStatus,
  note,
  voiceInfo,
  checkUserCanUseVoiceCloning,
  setOpenVoiceDetail,
  setReasonContent,
  setOpenViewReason,
  setViewReasonId,
  setIsShowContact,
  setViewReasonVoiceStatus,
  setViewReasonVoiceInfo,
  onContinueRecord,
  onShowUpgradePackage,
  handleDeleteVoice,
  isInstantVoiceCloning = true,
  setStepVoiceDetail,
  onOpenProvideEmailDialog,
  onContinueUploadVoice,
  onDuplicateVoice,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);

  const [anchorEl, setAnchorEl] = useState(null);

  const duplicateVoiceEnabled = getFeatureValue(FEATURE_KEYS.DUPLICATE_VOICE, {
    userId: user?.id,
    email: user?.email,
  });

  const hasProvidedEmail = checkUserHasProvidedEmail(user);

  const handleOpenMenu = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const renderVoiceStatus = (voiceStatus) => {
    const label = getVoiceStatusLabel(voiceStatus);
    const color = VOICE_STATUS_COLORS[voiceStatus];
    if (voiceStatus === VOICE_STATUS.RECORDING) {
      return <StyledChip color={color}>{t('gatheringAudio')}</StyledChip>;
    }

    if (!color || !label) return '';
    return <StyledChip color={color}>{t(label)}</StyledChip>;
  };

  const renderVoiceSubStatus = ({
    voiceStatus,
    voiceSubStatus,
    discardAt,
    showWaitingForRemoval = false,
  }) => {
    const enableSubStatusColor = [
      VOICE_STATUS.PRIVATE,
      VOICE_STATUS.PUBLIC,
    ].includes(voiceStatus);

    if (!enableSubStatusColor || !voiceSubStatus) return '';

    // Handle WAITING_FOR_REMOVAL separately based on showWaitingForRemoval flag
    if (voiceSubStatus === SUB_STATUS.WAITING_FOR_REMOVAL) {
      if (showWaitingForRemoval) {
        return (
          <WaitingForRemovalTag
            subStatus={voiceSubStatus}
            discardAt={discardAt}
          />
        );
      }
      return '';
    }

    // For other sub-statuses, only show when not in waiting for removal mode
    if (showWaitingForRemoval) return '';

    const label = getVoiceSubStatusLabel(voiceSubStatus);
    const color = SUB_STATUS_COLORS?.[voiceSubStatus];

    if (!color || !label) return '';

    return <StyledChip color={color}>{t(label)}</StyledChip>;
  };

  const onClickShareAndEarn = (e) => {
    e.stopPropagation();
    if (!hasProvidedEmail) {
      onOpenProvideEmailDialog(true);
      return;
    }
    dispatch(actions.voiceVoiceCloning.addVoice(voiceInfo));
    setStepVoiceDetail(STEP_VOICE_DETAIL.FORM_SHARE_AND_EARN);
    setOpenVoiceDetail(true);
  };

  const onClickRedirectVoice = () => {
    if (!checkUserCanUseVoiceCloning) {
      onShowUpgradePackage();
      return;
    }
    setStepVoiceDetail(STEP_VOICE_DETAIL.VOICE_DETAIL);
    dispatch(actions.voiceVoiceCloning.addVoice(voiceInfo));
    setOpenVoiceDetail(true);
  };

  const onClickShowReason = (event) => {
    event.stopPropagation();
    setReasonContent(note);
    setOpenViewReason(true);
    setViewReasonId(id);
    setIsShowContact(VOICE_NOT_USED.includes(status));
    if (setViewReasonVoiceStatus) setViewReasonVoiceStatus(status);
    if (setViewReasonVoiceInfo) setViewReasonVoiceInfo(voiceInfo);
  };

  const continueRecording = (e) => {
    e.stopPropagation();
    onContinueRecord(id);
  };

  const onClickContinue = (e) => {
    e.stopPropagation();
    if (onContinueUploadVoice) {
      onContinueUploadVoice(voiceInfo);
    }
  };

  const onClickUseVoice = (e) => {
    e?.stopPropagation();
    if (!checkUserCanUseVoiceCloning) {
      onShowUpgradePackage();
      return;
    }
    dispatch(actions.synthesisRequest.updateVoice(voiceInfo));
    history.push(route.TTS);
  };

  const onClickCopyVoiceCode = (e) => {
    e?.stopPropagation();
    navigator.clipboard.writeText(voiceInfo?.code || '');
    dispatch(
      actions.noti.push({
        severity: 'success',
        message: 'copiedVoiceCode',
      }),
    );
  };

  const onClickVoiceProfile = (e) => {
    e?.stopPropagation();
    window.open(`${LANDING_PAGE_URL}/voices/${voiceInfo?.code}`, '_blank');
  };

  const onClickDeleteVoice = (e) => {
    e?.stopPropagation();
    handleDeleteVoice(voiceInfo);
  };

  const onClickDuplicateVoice = (e) => {
    e?.stopPropagation();
    if (onDuplicateVoice) {
      onDuplicateVoice(id);
    }
  };

  const renderBadgeIcon = () => {
    if (isInstantVoiceCloning) {
      return (
        <OfflineBoltRounded
          sx={{
            color: COLOR.darkBlue,
            backgroundColor: COLOR.white,
            borderRadius: '50%',
          }}
        />
      );
    }
    return <img src={verifiedIcon} alt="badge" className="verified-icon" />;
  };

  // Get primary button configuration based on status and subStatus
  const getPrimaryButtonConfig = () => {
    const hasVoiceWithUpload =
      voiceInfo?.type === VOICE_CLONE_TYPE.FINETUNE_LONG_AUDIO;

    // Recording status
    if (status === VOICE_STATUS.RECORDING) {
      if (hasVoiceWithUpload) {
        return {
          label: t('continue'),
          onClick: onClickContinue,
          variant: 'outlineSmall',
        };
      }
      return {
        label: t('continue'),
        onClick: continueRecording,
        variant: 'outlineSmall',
      };
    }

    // Training status - no primary button
    if (status === VOICE_STATUS.TRAINING) {
      return null;
    }

    // Private status
    if (status === VOICE_STATUS.PRIVATE) {
      if (subStatus === SUB_STATUS.WAITING_FOR_APPROVAL) {
        return {
          label: t('voiceUse'),
          onClick: onClickUseVoice,
          variant: 'outlineSmall',
        };
      }
      // No subStatus or REJECTED subStatus
      if (!isInstantVoiceCloning) {
        return {
          label: t('shareAndEarn'),
          onClick: onClickShareAndEarn,
          variant: 'containedSmallNonBorder',
        };
      }
      return {
        label: t('voiceUse'),
        onClick: onClickUseVoice,
        variant: 'outlineSmall',
      };
    }

    // Public status
    if (status === VOICE_STATUS.PUBLIC) {
      return {
        label: t('voiceUse'),
        onClick: onClickUseVoice,
        variant: 'outlineSmall',
      };
    }

    // Suspended or Failed status
    if ([VOICE_STATUS.SUSPENDED, VOICE_STATUS.FAILED].includes(status)) {
      return {
        label: t('viewReason'),
        onClick: onClickShowReason,
        variant: 'outlineSmall',
      };
    }

    // Rejected status (from recording)
    if (status === VOICE_STATUS.REJECTED) {
      return {
        label: t('continueRecording'),
        onClick: continueRecording,
        variant: 'outlineSmall',
      };
    }

    return null;
  };

  // Get menu actions based on status and subStatus
  const getMenuActions = () => {
    const menuActionsList = [];

    // For instant voice cloning, only show copyVoiceCode and deleteVoice
    if (isInstantVoiceCloning) {
      if (status === VOICE_STATUS.PRIVATE) {
        menuActionsList.push({
          key: 'copyVoiceCode',
          onClick: onClickCopyVoiceCode,
        });
      }
      menuActionsList.push({ key: 'deleteVoice', onClick: onClickDeleteVoice });
      return menuActionsList;
    }

    // Recording status
    if (status === VOICE_STATUS.RECORDING) {
      if (duplicateVoiceEnabled) {
        menuActionsList.push({
          key: 'duplicateVoice',
          onClick: onClickDuplicateVoice,
        });
      }
      menuActionsList.push({ key: 'deleteVoice', onClick: onClickDeleteVoice });
      return menuActionsList;
    }

    // Private status
    if (status === VOICE_STATUS.PRIVATE) {
      if (subStatus === SUB_STATUS.WAITING_FOR_APPROVAL) {
        menuActionsList.push({
          key: 'copyVoiceCode',
          onClick: onClickCopyVoiceCode,
        });
        if (duplicateVoiceEnabled) {
          menuActionsList.push({
            key: 'duplicateVoice',
            onClick: onClickDuplicateVoice,
          });
        }
        return menuActionsList;
      }
      // No subStatus or REJECTED subStatus
      menuActionsList.push({ key: 'useVoice', onClick: onClickUseVoice });
      menuActionsList.push({
        key: 'copyVoiceCode',
        onClick: onClickCopyVoiceCode,
      });
      if (duplicateVoiceEnabled) {
        menuActionsList.push({
          key: 'duplicateVoice',
          onClick: onClickDuplicateVoice,
        });
      }
      menuActionsList.push({ key: 'deleteVoice', onClick: onClickDeleteVoice });
      return menuActionsList;
    }

    // Public status
    if (status === VOICE_STATUS.PUBLIC) {
      menuActionsList.push({
        key: 'copyVoiceCode',
        onClick: onClickCopyVoiceCode,
      });
      menuActionsList.push({
        key: 'voiceProfilePage',
        onClick: onClickVoiceProfile,
      });
      if (duplicateVoiceEnabled) {
        menuActionsList.push({
          key: 'duplicateVoice',
          onClick: onClickDuplicateVoice,
        });
      }
      return menuActionsList;
    }

    // Suspended or Failed status
    if ([VOICE_STATUS.SUSPENDED, VOICE_STATUS.FAILED].includes(status)) {
      if (duplicateVoiceEnabled && status === VOICE_STATUS.FAILED) {
        menuActionsList.push({
          key: 'duplicateVoice',
          onClick: onClickDuplicateVoice,
        });
      }
      menuActionsList.push({ key: 'deleteVoice', onClick: onClickDeleteVoice });
      return menuActionsList;
    }

    return menuActionsList;
  };

  const primaryButtonConfig = getPrimaryButtonConfig();
  const menuActions = getMenuActions();
  const showMenuButton = menuActions.length > 0;

  const renderPrimaryButton = () => {
    if (!primaryButtonConfig) return null;

    return (
      <CommonButton
        customStyle={{
          textTransform: 'none',
          height: '32px',
          borderRadius: '8px',
        }}
        variant={primaryButtonConfig.variant}
        textProps={{
          variantText: 'body-14-medium',
          color: COLOR.darkBlue,
        }}
        onClick={primaryButtonConfig.onClick}
      >
        {primaryButtonConfig.label}
      </CommonButton>
    );
  };

  return (
    <StyledVoiceItemCardNew
      className="voice"
      onClick={onClickRedirectVoice}
      id={`voice-${voiceInfo?.id}`}
    >
      <CommonAvatar
        avatar={avatar}
        badgeIcon={renderBadgeIcon()}
        size={64}
        className="badge-avatar"
        frameImageUrl={getFrameVoiceUrl(voiceInfo)}
      />
      <Box className="voice-info">
        <Box className="name-info">
          <Box className="name-info-left">
            <Typography variant="body-16-bold" color={COLOR.darkGray}>
              {name}
            </Typography>
            {renderVoiceSubStatus({
              voiceStatus: status,
              voiceSubStatus: subStatus,
              discardAt: voiceInfo?.discardAt,
              showWaitingForRemoval: false,
            })}
          </Box>
          <IconButton className="redirect-btn">
            <ChevronRightRounded fontSize="large" />
          </IconButton>
        </Box>
        <Box className="category-info">
          <Typography variant="body-14-regular">
            {getCategoryLabel(category?.name)}
          </Typography>
          {getCategoryLabel(category?.name) && gender && (
            <span className="category-dot">•</span>
          )}
          <Typography variant="body-14-regular">{t(gender)}</Typography>
        </Box>
        <Box className="status-action-row">
          <Box className="status">
            {renderVoiceStatus(status)}
            {renderVoiceSubStatus({
              voiceStatus: status,
              voiceSubStatus: subStatus,
              discardAt: voiceInfo?.discardAt,
              showWaitingForRemoval: true,
            })}
          </Box>
          <Box className="action-buttons">
            {!isMobile && renderPrimaryButton()}
            {showMenuButton && (
              <IconButton
                className="more-horiz-btn"
                onClick={handleOpenMenu}
                size="small"
              >
                <MoreVertRoundedIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
      <ActionMenuPopover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        menuActions={menuActions}
      />
    </StyledVoiceItemCardNew>
  );
};

export default VoiceItemCardNew;
